import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  CableDropbus, CableExternal, CableInternal, CabosAlimentacaoInterno, MitraNetworkParameters
} from '../../state/network-parameters.model.generated';
import { SimulationService } from '../../state/simulation.service';

@Component({
  selector: 'app-optical-cable',
  templateUrl: './optical-cable.component.html',
  styleUrls: ['./optical-cable.component.scss']
})
export class OpticalCableComponent implements OnInit {

  @Input()
  params: MitraNetworkParameters;

  @Input()
  parentForm: FormGroup;

  CableTypeInternal = CABLE_TYPE_INTERNAL;
  CableTypeAerial = CABLE_TYPE_AERIAL;
  CableTypeUnderground = CABLE_TYPE_UNDERGROUND;
  InternalCableConnector = INTERNAL_CABLE_CONNECTOR;
  Fibers = FIBERS;
  CableTypeDropbus = CABLE_TYPE_DROPBUS;

  internalFeedingCables = this.formBuilder.array([], [Validators.required]);
  feedingCables = this.formBuilder.array([], [Validators.required]);
  distributionCables = this.formBuilder.array([]);
  verticalAttendanceCables = this.formBuilder.array([], [Validators.required]);
  dropBusCables = this.formBuilder.array([]);

  cableTypesForm: FormGroup;
  form: FormGroup;

  showDropBusCables: boolean;

  public reloadInternalFeedingCables(cables: CabosAlimentacaoInterno) {
    this.internalFeedingCables = this.formBuilder.array([], [Validators.required]);
    cables.forEach(cable => this.formInternalFeedingCable(cable));
    this.reloadForm();
  }

  constructor(
    private formBuilder: FormBuilder,
    private simulationService: SimulationService
  ) { }

  ngOnInit(): void {
    this.showDropBusCables = this.simulationService.isHorizontalUnbalanced(this.params);
    this.create();
    this.createForms();
  }

  addInternalFeedingCable(cable = { connector_type: 'NONE'} as CableInternal): void {
    if (!this.params.internal_feeding_cables_types) {
      this.params.internal_feeding_cables_types = [cable];
    } else {
      this.params.internal_feeding_cables_types.push(cable);
    }
    this.formInternalFeedingCable(cable);
  }

  addFeedingCable(): void {
    const cable = {} as CableExternal;

    if (!this.params.feeding_cables_types) {
      this.params.feeding_cables_types = [cable];
    } else {
      this.params.feeding_cables_types.push(cable);
    }
    this.formFeedingCable(cable);
  }

  addDistributionCable(): void {
    const cable = {} as CableExternal;

    if (!this.params.distribution_cables_types) {
      this.params.distribution_cables_types = [cable];
    } else {
      this.params.distribution_cables_types.push(cable);
    }
    this.formDistributionCable(cable);
  }

  addVerticalAttendanceCable(): void {
    const cable = {} as CableExternal;

    if (!this.params.vertical_attendance_cable_types) {
      this.params.vertical_attendance_cable_types = [cable];
    } else {
      this.params.vertical_attendance_cable_types.push(cable);
    }
    this.formVerticalAttendanceCable(cable);
  }

  addDropBusCable(cable = {} as CableDropbus): void {
    if (!this.params.drop_bus_cables_types) {
      this.params.drop_bus_cables_types = [cable];
    } else {
      this.params.drop_bus_cables_types.push(cable);
    }
    this.formDropBusCable(cable);
  }

  removeVerticalAttendanceCable(index: number): void {
    this.verticalAttendanceCables.removeAt(index);
    this.params.vertical_attendance_cable_types.splice(index, 1);
  }

  removeInternalFeedingCable(index: number): void {
    this.internalFeedingCables.removeAt(index);
    this.params.internal_feeding_cables_types.splice(index, 1);
  }

  removeFeedingCable(index: number): void {
    this.feedingCables.removeAt(index);
    this.params.feeding_cables_types.splice(index, 1);
  }

  removeDistributionCable(index: number): void {
    this.distributionCables.removeAt(index);
    this.params.distribution_cables_types.splice(index, 1);
  }

  removeDropBusCable(index: number): void {
    this.dropBusCables.removeAt(index);
    this.params.drop_bus_cables_types.splice(index, 1);
  }

  private formInternalFeedingCable(cable: CableInternal): void {
    this.internalFeedingCables.push(this.createCableInternalForm(cable));
  }

  private formFeedingCable(cable: CableExternal): void {
    this.feedingCables.push(this.createCableExternalForm(cable));
  }

  private formDistributionCable(cable: CableExternal): void {
    this.distributionCables.push(this.createCableExternalForm(cable));
  }

  private formVerticalAttendanceCable(cable: CableExternal): void {
    this.verticalAttendanceCables.push(this.createCableExternalForm(cable));
  }

  private formDropBusCable(cable: CableDropbus): void {
    this.dropBusCables.push(this.createCableDropbus(cable));
  }

  private createForms() {
    this.parentForm.addControl('cables', this.form);

    if (this.params.internal_feeding_cables_types) {
      this.params.internal_feeding_cables_types
        .forEach(cable => this.formInternalFeedingCable(cable));
    }

    if (this.params.feeding_cables_types) {
      this.params.feeding_cables_types.forEach(cable => this.formFeedingCable(cable));
    }

    if (this.params.distribution_cables_types) {
      this.params.distribution_cables_types.forEach(cable => this.formDistributionCable(cable));
    }

    if (this.params.vertical_attendance_cable_types) {
      this.params.vertical_attendance_cable_types.forEach(cable => this.formVerticalAttendanceCable(cable));
    }

    if (this.params.drop_bus_cables_types) {
      this.params.drop_bus_cables_types.forEach(cable => this.formDropBusCable(cable));
    }

    this.form.patchValue({ max_cables_per_edge: this.params.max_cables_per_edge });

    this.form.get('max_cables_per_edge').valueChanges.subscribe((value) => {
      this.params.max_cables_per_edge = Number(value);
    });

    this.updateCableTypeForm();
  }

  private updateCableTypeForm() {
    this.cableTypesForm.patchValue(this.params.cables_types);

    this.cableTypesForm.valueChanges.subscribe(values => {
      this.params.cables_types = {
        distribution_aerial: values.distribution_aerial,
        drop_bus_aerial: values.drop_bus_aerial ? values.drop_bus_aerial : undefined,
        distribution_underground: values.distribution_underground,
        feeding_aerial: values.feeding_aerial,
        feeding_underground: values.feeding_underground,
        internal_underground: values.internal_underground,
        vertical_termination_aerial: values.vertical_termination_aerial,
        vertical_termination_underground: values.vertical_termination_underground
      };
    });
  }

  private createCableInternalForm(cable: CableInternal): FormGroup {
    const group = this.formBuilder.group({
      fibers: [cable.fibers, [Validators.required, Validators.min(1)]],
      connector_type: [cable.connector_type, Validators.required],
    });

    group.valueChanges.subscribe((values) => {
      // TODO: remover
      cable.name_pattern = 'initials';
      cable.fibers = values.fibers;
      cable.connector_type = values.connector_type;

      if (this.internalFeedingCables.length > 0 && this.internalFeedingCables.controls[0] === group) {
        console.log("First cable changed - updating other cables");
        for (let i = 1; i < this.internalFeedingCables.controls.length; i++) {
          this.internalFeedingCables.controls[i].get('connector_type').setValue(values.connector_type);
        }
      }
    });

    return group;
  }

  private createCableExternalForm(cable: CableExternal): AbstractControl {
    const group = this.formBuilder.group({
      fibers: [cable.fibers, Validators.required],
      reserve: [cable.reserve, [Validators.required, Validators.min(0)]],
    });
    group.setValidators(FiberCableGreaterThanReserve);

    group.valueChanges.subscribe((values) => {
      // TODO: remover
      cable.name_pattern = 'initials';
      cable.fibers = values.fibers;
      cable.reserve = values.reserve;
    });

    return group;
  }

  private createCableDropbus(cable: CableDropbus) {
    const group = this.formBuilder.group({
      length: [cable.length, Validators.required],
      connector_type: [cable.connector_type, Validators.required],
    });

    group.valueChanges.subscribe((values) => {
      cable.name_pattern = 'initials';
      cable.length = values.length;
      cable.connector_type = values.connector_type;

      if (this.dropBusCables.length > 0 && this.dropBusCables.controls[0] === group) {
        console.log('First cable changed - updating other cables');
        for (let i = 1; i < this.dropBusCables.controls.length; i++) {
          this.dropBusCables.controls[i].get('connector_type').setValue(values.connector_type);
        }
      }
    });

    return group;
  }

  private create(): void {
    this.cableTypesForm = this.formBuilder.group({
      internal_underground: ['', Validators.required],
      feeding_underground: ['', Validators.required],
      feeding_aerial: ['', Validators.required],
      distribution_underground: ['', Validators.required],
      distribution_aerial: ['', Validators.required],
      vertical_termination_underground: ['', Validators.required],
      vertical_termination_aerial: ['', Validators.required],
      drop_bus_aerial: ['', this.showDropBusCables ? Validators.required : []]
    });

    this.reloadForm();
  }

  private reloadForm() {
    this.form = this.formBuilder.group({
      internalFeedingCables: this.internalFeedingCables,
      feedingCables: this.feedingCables,
      distributionCables: this.distributionCables,
      verticalAttendanceCables: this.verticalAttendanceCables,
      dropBusCables: this.dropBusCables,
      max_cables_per_edge: [this.params.max_cables_per_edge ? '' : this.params.max_cables_per_edge, [Validators.required, Validators.min(1)]],
      cableTypes: this.cableTypesForm
    });
    console.log(this.internalFeedingCables);
    this.parentForm.removeControl('cables');
    this.parentForm.addControl('cables', this.form);
    this.form.patchValue({ max_cables_per_edge: this.params.max_cables_per_edge });
  }

  getLastCableInternal() {
    if (this.params.internal_feeding_cables_types) {
      return { connector_type: this.params.internal_feeding_cables_types[this.params.internal_feeding_cables_types.length - 1].connector_type} as CableInternal;
    } else {
      return { connector_type: 'NONE'} as CableInternal;
    }
  }

  getLastCableDrop(): CableDropbus {
    if (this.params.drop_bus_cables_types) {
      return { connector_type: this.params.drop_bus_cables_types[this.params.drop_bus_cables_types.length - 1].connector_type} as CableDropbus;
    } else {
      return { } as CableDropbus;
    }
  }
}


const FiberCableGreaterThanReserve: ValidatorFn = (fg: FormGroup) => {
  const fibers = fg.get('fibers').value as number;
  const reserve = fg.get('reserve').value as number;

  if (fibers > reserve) {
    return;
  }
  return { fibersSmallerReserve: true };
};


const CABLE_TYPE_INTERNAL = ['DD-LSZH'];

const CABLE_TYPE_AERIAL = [
  'AS-80-G-NR',
  'AS-80-G-RC',
  'AS-80-S-NR',
  'AS-80-S-RC',
  'AS-80-TS-NR',
  'AS-80-TS-RC',
  'AS-120-G-NR',
  'AS-120-G-RC',
  'AS-120-S-NR',
  'AS-120-S-RC',
  'AS-120-TS-NR',
  'AS-120-TS-RC',
  'ASU-80-S-NR',
  'ASU-80-S-RC',
  'ASU-120-S-NR',
  'ASU-120-S-RC',
];
const CABLE_TYPE_UNDERGROUND = [
  'DD-G-NR',
  'DD-G-RC',
  'DD-S-NR',
  'DD-S-RC',
  'DD-TS-NR',
  'DD-TS-RC',
  'ASU-80-S-NR'
];

const CABLE_TYPE_DROPBUS = ['AS-80-LSZH-BLI'];

const INTERNAL_CABLE_CONNECTOR = ['SC-APC', 'LC-APC', 'E2000-APC'];

const FIBERS = [1, 2, 4, 6, 8, 12, 18, 24, 36, 48, 72, 144, 288];
